import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  HiOutlineUser,
  HiOutlineLockClosed,
  HiOutlinePhone,
  HiOutlineBriefcase,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineCheck,
  HiOutlineExclamationCircle,
  HiOutlineShieldCheck,
  HiOutlineX
} from 'react-icons/hi';
import Toast from '../components/ui/Toast';
import { useToast } from '../hooks/useToast';
import { useAuth } from '@/context/AuthContext';
import authProfileService from './../services/authProfileService';
import type { UpdateProfileRequest, ChangePasswordRequest } from './../services/authProfileService';
type TabType = 'perfil' | 'contrasena';
type UserRole = 'operator' | 'comissionist' | 'adviser' | 'none';

function getServerErrorMessage(err: unknown, fallback: string): string {
  const error = err as any;
  return error?.response?.data?.message || error?.message || fallback;
}

const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: 'operator', label: 'Operador' },
  { value: 'comissionist', label: 'Comisionista' },
  { value: 'adviser', label: 'Asesor' },
  { value: 'none', label: 'Sin rol' },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
}

interface PasswordRule {
  key: string;
  label: string;
  test: (password: string) => boolean;
}

const PASSWORD_RULES: PasswordRule[] = [
  { key: 'length', label: 'Al menos 14 caracteres', test: (p) => p.length >= 14 },
  { key: 'uppercase', label: 'Al menos una mayúscula', test: (p) => /[A-Z]/.test(p) },
  { key: 'lowercase', label: 'Al menos una minúscula', test: (p) => /[a-z]/.test(p) },
  { key: 'number', label: 'Al menos un número', test: (p) => /\d/.test(p) },
  { key: 'special', label: 'Al menos un carácter especial', test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(p) },
];

function usePasswordStrength(password: string) {
  return useMemo(() => {
    const results = PASSWORD_RULES.map((rule) => ({
      ...rule,
      passed: rule.test(password),
    }));
    const passedCount = results.filter((r) => r.passed).length;
    const total = results.length;
    const allPassed = passedCount === total;

    let color = 'var(--text-muted)';
    let label = '';

    if (password.length === 0) {
    } else if (passedCount <= 2) {
      color = '#ef4444';
      label = 'Débil';
    } else if (passedCount <= 3) {
      color = '#f59e0b';
      label = 'Regular';
    } else if (passedCount <= 4) {
      color = '#7ab900';
      label = 'Buena';
    } else {
      color = '#22c55e';
      label = 'Fuerte';
    }

    return { results, passedCount, total, allPassed, color, label };
  }, [password]);
}

function PasswordStrengthIndicator({ password }: { password: string }) {
  const { results, passedCount, total, color, label } = usePasswordStrength(password);

  if (password.length === 0) return null;

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
        {Array.from({ length: total }, (_, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: '4px',
              borderRadius: '2px',
              background: i < passedCount ? color : 'var(--border-color)',
              transition: 'background 0.3s ease',
            }}
          />
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color }}>
          {label}
        </span>
        <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
          {passedCount}/{total} requisitos
        </span>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '10px 12px',
        background: 'var(--bg-body)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-light)',
      }}>
        {results.map((rule) => (
          <div
            key={rule.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '0.75rem',
              color: rule.passed ? '#22c55e' : 'var(--text-muted)',
              transition: 'color 0.2s ease',
            }}
          >
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: rule.passed ? 'rgba(34, 197, 94, 0.12)' : 'rgba(156, 163, 175, 0.12)',
              flexShrink: 0,
              transition: 'background 0.2s ease',
            }}>
              {rule.passed ? (
                <HiOutlineCheck style={{ fontSize: '0.625rem' }} />
              ) : (
                <HiOutlineX style={{ fontSize: '0.625rem' }} />
              )}
            </span>
            {rule.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function ConfigurationPage(): JSX.Element {
  const [activeTab, setActiveTab] = useState<TabType>('perfil');
  const isMobile = useIsMobile();

  const tabs: { id: TabType; label: string; icon: React.ComponentType<{ style?: React.CSSProperties }> }[] = [
    { id: 'perfil', label: 'Perfil', icon: HiOutlineUser },
    { id: 'contrasena', label: 'Contraseña', icon: HiOutlineLockClosed }
  ];

  return (
    <div className="fade-in" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="page-header" style={{ marginBottom: '0.75rem', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
        <div>
          <h1 className="page-title" style={{ fontSize: isMobile ? '1.25rem' : '1.5rem', margin: 0, lineHeight: 1.2 }}>
            Configuración
          </h1>
          {!isMobile && <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: '2px 0 0' }}>Administra tu perfil y seguridad</p>}
        </div>
      </div>

      <div style={{ marginBottom: '0.75rem', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-body)', padding: '4px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-color)', width: isMobile ? '100%' : 'fit-content' }}>
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: isMobile ? '12px 16px' : '10px 20px', border: 'none', borderRadius: 'var(--radius-md)', background: activeTab === tab.id ? 'var(--bg-card)' : 'transparent', color: activeTab === tab.id ? 'var(--primary)' : 'var(--text-secondary)', fontWeight: activeTab === tab.id ? 600 : 500, fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: activeTab === tab.id ? 'var(--shadow-sm)' : 'none', flex: isMobile ? 1 : 'unset' }}>
              <tab.icon style={{ fontSize: '1.125rem' }} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {activeTab === 'perfil' && <ProfileTab />}
        {activeTab === 'contrasena' && <PasswordTab />}
      </div>
    </div>
  );
}

function ProfileTab(): JSX.Element {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { toastState, showSuccess: toastSuccess, showError: toastError, closeToast } = useToast();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    userRole: 'none' as UserRole,
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      setFetching(true);
      const response = await authProfileService.getProfile();
      if (response.data) {
        setFormData({
          firstName: response.data.firstName ?? '',
          lastName: response.data.lastName ?? '',
          email: response.data.email ?? '',
          userRole: (response.data.userRole as UserRole) ?? 'none',
          phone: response.data.phone ?? '',
        });
      }
    } catch (err) {
      toastError(getServerErrorMessage(err, 'No se pudo cargar el perfil'));
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => { void fetchProfile(); }, [fetchProfile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toastError('El nombre y apellido son obligatorios');
      return;
    }
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      toastError('El formato del correo electrónico no es válido');
      return;
    }
    setLoading(true);
    try {
      const payload: UpdateProfileRequest = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        userRole: formData.userRole,
        phone: formData.phone.trim(),
      };
      await authProfileService.updateProfile(payload);
      toastSuccess({ title: '¡Éxito!', message: 'Perfil actualizado correctamente' });
    } catch (err) {
      toastError(getServerErrorMessage(err, 'Error al actualizar el perfil'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="card card-glow" style={{ maxWidth: '720px' }}>
        <div className="card-header" style={{ background: 'transparent', border: 'none', padding: isMobile ? '1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--border-light)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: 'rgba(101, 45, 134, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '1.25rem', flexShrink: 0 }}>
              <HiOutlineUser />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 600 }}>Información del perfil</h3>
              <p style={{ margin: '2px 0 0', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Actualiza tus datos personales</p>
            </div>
          </div>
        </div>

        <div className="card-body" style={{ padding: isMobile ? '1rem' : '1.5rem' }}>
          {fetching ? (
            <div style={{ padding: '3rem', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', color: 'var(--text-muted)' }}>
              <div className="spinner" />
              <span>Cargando perfil…</span>
            </div>
          ) : (
            <form onSubmit={e => { void handleSubmit(e); }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    Nombre <span style={{ color: 'var(--accent-pink)' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Tu nombre"
                    required
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    Apellido <span style={{ color: 'var(--accent-pink)' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Tu apellido"
                    required
                  />
                </div>

                <div style={{ gridColumn: isMobile ? 'auto' : '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                    Correo electrónico
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="correo@ejemplo.com"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Teléfono</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="+52 000 000 0000"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Rol</label>
                  <select
                    name="userRole"
                    value={formData.userRole}
                    onChange={handleChange}
                    className="form-control"
                  >
                    {ROLE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button type="submit" className="btn btn-primary" disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {loading ? (
                    <><div className="spinner" style={{ width: '16px', height: '16px' }} /> Guardando...</>
                  ) : (
                    'Guardar cambios'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      <Toast {...toastState} onClose={closeToast} />
    </>
  );
}

function PasswordTab(): JSX.Element {
  const isMobile = useIsMobile();
  const { toastState, showSuccess: toastSuccess, showError: toastError, closeToast } = useToast();

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { allPassed } = usePasswordStrength(formData.newPassword);

  const passwordsMatch = formData.newPassword.length > 0 && formData.confirmPassword.length > 0 && formData.newPassword === formData.confirmPassword;
  const passwordsMismatch = formData.confirmPassword.length > 0 && formData.newPassword !== formData.confirmPassword;

  const isFormValid =
    formData.currentPassword.length > 0 &&
    allPassed &&
    passwordsMatch;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allPassed) {
      toastError('La contraseña no cumple con todos los requisitos de seguridad');
      return;
    }
    if (!passwordsMatch) {
      toastError('Las contraseñas no coinciden');
      return;
    }
    setLoading(true);
    try {
      const payload: ChangePasswordRequest = {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      };
      await authProfileService.changePassword(payload);
      toastSuccess({ title: '¡Éxito!', message: 'Contraseña actualizada correctamente' });
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    } catch (err) {
      toastError(getServerErrorMessage(err, 'Error al cambiar la contraseña. Verifica tu contraseña actual.'));
    } finally {
      setLoading(false);
    }
  };

  const renderPasswordField = (
    id: string,
    name: string,
    label: string,
    value: string,
    show: boolean,
    onToggle: () => void,
    borderColor?: string
  ) => (
    <div>
      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
        {label} <span style={{ color: 'var(--accent-pink)' }}>*</span>
      </label>
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          name={name}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={handleChange}
          className="form-control"
          placeholder={label}
          required
          style={{
            paddingRight: '42px',
            borderColor: borderColor || undefined,
          }}
        />
        <button
          type="button"
          onClick={onToggle}
          style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px 6px', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', fontSize: '1.1rem' }}
        >
          {show ? <HiOutlineEyeOff /> : <HiOutlineEye />}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="card card-glow" style={{ maxWidth: '720px' }}>
        <div className="card-header" style={{ background: 'transparent', border: 'none', padding: isMobile ? '1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--border-light)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: 'rgba(101, 45, 134, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '1.25rem', flexShrink: 0 }}>
              <HiOutlineShieldCheck />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 600 }}>Cambiar contraseña</h3>
              <p style={{ margin: '2px 0 0', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Asegúrate de usar una contraseña segura</p>
            </div>
          </div>
        </div>

        <div className="card-body" style={{ padding: isMobile ? '1rem' : '1.5rem' }}>
          <form onSubmit={e => { void handleSubmit(e); }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {renderPasswordField(
                'currentPassword', 'currentPassword', 'Contraseña actual',
                formData.currentPassword, showCurrentPassword,
                () => setShowCurrentPassword(v => !v)
              )}

              {renderPasswordField(
                'newPassword', 'newPassword', 'Nueva contraseña',
                formData.newPassword, showNewPassword,
                () => setShowNewPassword(v => !v),
                formData.newPassword.length > 0 ? (allPassed ? '#22c55e' : undefined) : undefined
              )}

              <PasswordStrengthIndicator password={formData.newPassword} />

              {renderPasswordField(
                'confirmPassword', 'confirmPassword', 'Confirmar contraseña',
                formData.confirmPassword, showConfirmPassword,
                () => setShowConfirmPassword(v => !v),
                passwordsMismatch ? '#ef4444' : passwordsMatch ? '#22c55e' : undefined
              )}

              {passwordsMismatch && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8125rem', color: '#ef4444', marginTop: '-0.5rem' }}>
                  <HiOutlineX style={{ fontSize: '0.95rem', flexShrink: 0 }} />
                  Las contraseñas no coinciden
                </div>
              )}
              {passwordsMatch && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8125rem', color: '#22c55e', marginTop: '-0.5rem' }}>
                  <HiOutlineCheck style={{ fontSize: '0.95rem', flexShrink: 0 }} />
                  Las contraseñas coinciden
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !isFormValid}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: isFormValid ? 1 : 0.5,
                  cursor: isFormValid && !loading ? 'pointer' : 'not-allowed',
                }}
              >
                {loading ? (
                  <><div className="spinner" style={{ width: '16px', height: '16px' }} /> Actualizando...</>
                ) : (
                  'Cambiar contraseña'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      <Toast {...toastState} onClose={closeToast} />
    </>
  );
}

export default ConfigurationPage;